import {
  type Ack,
  type ChatItemDto,
  type PublicRoomState,
  type RoomFx,
  type SocketEvent,
  type SocketPayload,
} from '@spin/shared';
import { io, type Socket } from 'socket.io-client';
import { tNow } from '../i18n';
import { useRoom } from '../store/room';
import { useSession } from '../store/session';
import { useUi, wheelSpin } from '../store/ui';
import { ApiError, http } from './http';

export interface Snapshot {
  room: PublicRoomState | null;
  chat: ChatItemDto[];
  serverNow: number;
}

type Listener = (fx: RoomFx & { v: number }) => void;

/**
 * Realtime connection to the game server (Socket.IO /game). Reconnects by itself; after every (re)connect the table
 * is re-synced, so a dropped connection or a server restart never leaves the player with a stale table.
 */
class GameClient {
  private socket: Socket | null = null;
  private fxListeners = new Set<Listener>();
  /** Server events handled in other modules (calls); attached to every connection. */
  private handlers = new Map<string, (...args: unknown[]) => void>();
  reauth: (() => Promise<string | null>) | null = null;

  on<T>(event: string, handler: (payload: T) => void): void {
    this.handlers.set(event, handler as (...args: unknown[]) => void);
    this.socket?.on(event, handler);
  }

  connect(): void {
    if (this.socket) {
      if (!this.socket.connected) this.socket.connect();
      return;
    }
    const socket = io('/game', {
      transports: ['websocket'],
      auth: (cb) => cb({ token: http.getToken() }),
      reconnectionDelay: 500,
      reconnectionDelayMax: 5_000,
    });
    this.socket = socket;
    for (const [event, handler] of this.handlers) socket.on(event, handler);

    socket.on('connect', () => {
      useSession.getState().setReplaced(false);
      void this.sync();
    });
    socket.on('connect_error', async (err: Error & { data?: { code?: string } }) => {
      const code = err.data?.code ?? err.message;
      if ((code === 'TOKEN_EXPIRED' || code === 'UNAUTHORIZED') && this.reauth) {
        const token = await this.reauth();
        if (token) socket.connect();
      } else if (code === 'BANNED') {
        useSession.getState().setBanned();
      }
    });
    socket.on('disconnect', (reason) => {
      // "io server disconnect" = the server ended our session on purpose (another device took over).
      if (reason === 'io server disconnect') useSession.getState().setReplaced(true);
    });
    socket.on('room.state', (m: { state: PublicRoomState; serverNow: number }) =>
      useRoom.getState().applyState(m.state, m.serverNow),
    );
    socket.on('room.fx', (fx: RoomFx & { v: number }) => {
      useRoom.getState().pushFx(fx);
      this.fxListeners.forEach((l) => l(fx));
    });
    socket.on('chat.msg', (item: ChatItemDto) => useRoom.getState().addChat(item));
    socket.on('user.wallet', (m: { balance: number; gm: number }) => useSession.getState().setWallet(m));
    socket.on('room.left', (m: { reason: string }) => {
      useRoom.getState().clear();
      const session = useSession.getState();
      if (session.stack.at(-1)?.name === 'table') session.reset({ name: 'portal' });
      if (m.reason === 'idle') useUi.getState().toast(tNow('table.idle_kicked'), 'info');
    });
    socket.on('session.replaced', () => useSession.getState().setReplaced(true));
    socket.on('server.restarting', () => useUi.getState().toast(tNow('common.reconnecting')));
    socket.on('user.sanction', (m: { kind: string }) => {
      if (m.kind === 'banned') useSession.getState().setBanned();
      if (m.kind === 'muted') useUi.getState().toast(tNow('errors.MUTED'), 'error');
    });
    socket.on('notify', (n: Record<string, unknown> & { kind: string }) => notify(n));
    socket.on('wheel.win', (m: { name: string; photoUrl: string | null; prize: string }) => {
      // The player's own big win is shown by the wheel itself once it stops.
      if (Date.now() < wheelSpin.until) return;
      useUi.getState().showWinner(m);
    });
  }

  /** Drops the connection for good (web logout). */
  disconnect(): void {
    this.socket?.disconnect();
    this.socket = null;
    useRoom.getState().clear();
  }

  get connected(): boolean {
    return Boolean(this.socket?.connected);
  }

  onFx(listener: Listener): () => void {
    this.fxListeners.add(listener);
    return () => this.fxListeners.delete(listener);
  }

  /** Emits an event and resolves with the ack data, or throws ApiError. */
  async emit<E extends SocketEvent, T = unknown>(event: E, payload: SocketPayload<E>): Promise<T> {
    if (!this.socket) throw new ApiError('SERVICE_UNAVAILABLE', 'Not connected');
    let ack: Ack<T>;
    try {
      ack = (await this.socket.timeout(8_000).emitWithAck(event, payload)) as Ack<T>;
    } catch {
      throw new ApiError('SERVICE_UNAVAILABLE', 'No answer from the server');
    }
    if (!ack.ok) throw new ApiError(ack.error.code, ack.error.message, 0, ack.error.retryAfterMs);
    return ack.data;
  }

  async sync(): Promise<void> {
    try {
      const snap = await this.emit<'room.sync', Snapshot>('room.sync', {});
      useRoom.getState().applySnapshot(snap.room, snap.chat, snap.serverNow);
      const session = useSession.getState();
      if (snap.room) {
        // Still seated after a reload or reconnect: back to the table from anywhere in the portal.
        if (session.stack.at(-1)?.name === 'portal') session.reset({ name: 'table' });
      } else if (session.stack.at(-1)?.name === 'table') {
        await this.takeSeat();
      }
    } catch {
      /* the next reconnect tries again */
    }
  }

  private seating: Promise<void> | null = null;

  /**
   * Sits the player down when the table screen has no table yet: at the table of an invite link if there is one,
   * otherwise wherever there is room (the reference's PLAY opens straight into a table).
   */
  takeSeat(): Promise<void> {
    this.seating ??= (async () => {
      try {
        if (useRoom.getState().room) return;
        const invite = sessionStorage.getItem('spin:join-table');
        sessionStorage.removeItem('spin:join-table');
        if (invite) await this.join(invite).catch(() => this.quickJoin());
        else await this.quickJoin();
      } finally {
        this.seating = null;
      }
    })();
    return this.seating;
  }

  async quickJoin(): Promise<Snapshot> {
    const snap = await this.emit<'room.quickJoin', Snapshot>('room.quickJoin', {});
    useRoom.getState().applySnapshot(snap.room, snap.chat, snap.serverNow);
    return snap;
  }

  async join(roomId: string): Promise<Snapshot> {
    const snap = await this.emit<'room.join', Snapshot>('room.join', { roomId });
    useRoom.getState().applySnapshot(snap.room, snap.chat, snap.serverNow);
    return snap;
  }

  async change(): Promise<Snapshot> {
    const snap = await this.emit<'room.change', Snapshot>('room.change', {});
    useRoom.getState().applySnapshot(snap.room, snap.chat, snap.serverNow);
    return snap;
  }

  async leave(): Promise<void> {
    await this.emit('room.leave', {});
    useRoom.getState().clear();
  }
}

function notify(n: Record<string, unknown> & { kind: string }): void {
  const { toast } = useUi.getState();
  switch (n.kind) {
    case 'admired': {
      // At the table with nothing else open: the reference's "Courting" popup; otherwise a toast.
      const ui = useUi.getState();
      if (useSession.getState().stack.at(-1)?.name === 'table' && !ui.dialog)
        ui.openDialog('courting', {
          userId: String(n.userId ?? ''),
          name: String(n.name),
          price: Number(n.price),
        });
      else toast(tNow('notify.admired', { name: String(n.name), price: Number(n.price) }), 'success');
      break;
    }
    case 'outbid':
      toast(tNow('notify.outbid', { name: String(n.name), target: String(n.target) }));
      break;
    case 'gift':
      toast(tNow('notify.gift', { name: String(n.name), emoji: String(n.emoji) }), 'success');
      break;
    case 'achievement':
      toast(
        `${String(n.icon)} ${tNow('notify.achievement', {
          title: tNow(`achievement.${String(n.code)}.title`),
          level: Number(n.level ?? 1),
          reward: Number(n.reward),
        })}`,
        'success',
      );
      break;
    case 'turned_down':
      toast(tNow('notify.turned_down', { name: String(n.name) }));
      break;
    case 'friend_request':
      toast(`🤝 ${tNow('notify.friend_request', { name: String(n.name) })}`, 'success');
      break;
    case 'friend_accepted':
      toast(`🤝 ${tNow('notify.friend_accepted', { name: String(n.name) })}`, 'success');
      break;
    case 'activity_reward':
      toast(tNow('activity.reward_toast', { hearts: Number(n.hearts) }), 'success');
      break;
    case 'activity_top':
      toast(tNow('activity.top_toast', { place: Number(n.place), hearts: Number(n.hearts) }), 'success');
      break;
    case 'referral':
      toast(tNow('notify.referral', { bonus: Number(n.bonus), name: '🎉' }), 'success');
      break;
    case 'gm':
      toast(tNow('notify.gm', { amount: Number(n.amount) }), 'success');
      break;
    case 'transfer_gm':
      toast(tNow('notify.transfer_gm', { name: String(n.name), amount: Number(n.amount) }), 'success');
      break;
    case 'transfer_hearts':
      toast(tNow('notify.transfer_hearts', { name: String(n.name), amount: Number(n.amount) }), 'success');
      break;
    case 'league_result': {
      const chest = n.chest ? tNow(`league.chest.${String(n.chest)}`) : '';
      toast(tNow('league.result_toast', { place: Number(n.place), chest }), n.chest ? 'success' : 'info');
      break;
    }
  }
}

export const game = new GameClient();
